// Item definitions as data (decision 0053: one item table). Phase C built what gathering and drops need: ids, names
// through the game's strings, a stack size and tags. Phase D1 grows the row (every new field optional): the inventory
// tab it sorts into, its rarity tier, its price in the one currency, the equipment slot kind it fits, the modifiers it
// adds while equipped (on the @rpg/abilities base), the effects using it applies (a dish's buff, a tonic's healing),
// and the levels a forge can raise it through. Icons are the game's (drawn in code).
import type { Modifier } from "@rpg/abilities";

/** One item as the game's table defines it. */
export interface ItemDef {
  id: string;
  /** The name's string id (the game's strings table formats it). */
  name: string;
  /** How many fit in one stack (1: never stacks). */
  stack: number;
  /** Free tags ("herb", "ore", "material", "food"): crafting, filters and tabs read them. */
  tags: readonly string[];
  /** The inventory tab it sorts into (the game's tab ids: weapons, materials, consumables, key items). Default
   *  `DEFAULT_TAB`. */
  tab?: string;
  /** Its rarity tier (one of the table's `rarities` when the table names them). */
  rarity?: string;
  /** What a shop sells it for, in the one currency. Absent: shops neither sell nor buy it. */
  price?: number;
  /** The equipment slot kind it fits ("weapon", "charm"). Absent: it can't be equipped. */
  slot?: string;
  /** Modifiers it adds while equipped. */
  modifiers?: readonly Modifier[];
  /** Effects (the ability set's ids) using one applies, then it is spent. Absent: it can't be used. */
  use?: readonly string[];
  /** Levels a forge raises it through: `levels[0]` is level 1's extra modifiers (usually none), `levels[k]` level
   *  k + 1's, each in place of the last. Absent: one level. */
  levels?: readonly { modifiers: readonly Modifier[] }[];
}

/** The tab an item sorts into when its row names none. */
export const DEFAULT_TAB = "items";

/** What the table checks rows against: the rarity tiers, the stat set and the effects, when the game names them. */
export interface ItemTableOptions {
  rarities?: readonly string[];
  attributes?: readonly string[];
  /** The ability set's effect ids, for `use`. */
  effects?: readonly string[];
}

/** Problems in an item list, one line each (empty when clean). */
export function checkItems(defs: readonly ItemDef[], o: ItemTableOptions = {}): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const d of defs) {
    if (!d.id) out.push("an item has no id");
    if (seen.has(d.id)) out.push(`item "${d.id}" is defined twice`);
    seen.add(d.id);
    if (!Number.isInteger(d.stack) || d.stack < 1) out.push(`item "${d.id}": stack ${d.stack} is not a whole number of at least 1`);
    if (!d.name) out.push(`item "${d.id}" has no name`);
    if (d.rarity !== undefined && o.rarities && !o.rarities.includes(d.rarity)) out.push(`item "${d.id}": unknown rarity "${d.rarity}"`);
    if (d.price !== undefined && !(Number.isInteger(d.price) && d.price >= 0)) out.push(`item "${d.id}": price ${d.price} is not a whole number ≥ 0`);
    const mods = [...(d.modifiers ?? []), ...(d.levels ?? []).flatMap((l) => l.modifiers)];
    if (o.attributes) for (const m of mods) if (!o.attributes.includes(m.attribute)) out.push(`item "${d.id}" modifies unknown stat "${m.attribute}"`);
    if (o.effects) for (const e of d.use ?? []) if (!o.effects.includes(e)) out.push(`item "${d.id}" uses unknown effect "${e}"`);
    if (mods.length && !d.slot) out.push(`item "${d.id}" has modifiers but no slot`);
    if (d.levels?.length && d.stack !== 1) out.push(`item "${d.id}": an item with forge levels must not stack`);
  }
  return out;
}

/** The item table, checked. */
export class ItemTable {
  private readonly byId: Map<string, ItemDef>;

  /** Throws listing every problem `checkItems` finds. */
  constructor(defs: readonly ItemDef[], o: ItemTableOptions = {}) {
    const issues = checkItems(defs, o);
    if (issues.length) throw new Error(`items: ${issues.join("; ")}`);
    this.byId = new Map(defs.map((d) => [d.id, { ...d, tags: [...d.tags] }]));
  }

  has(id: string): boolean {
    return this.byId.has(id);
  }

  /** The item's definition. Throws on an unknown id. */
  get(id: string): ItemDef {
    const d = this.byId.get(id);
    if (!d) throw new Error(`items: no item "${id}"`);
    return d;
  }

  /** Every item, in table order. */
  list(): ItemDef[] {
    return [...this.byId.values()];
  }

  /** The items carrying a tag. */
  tagged(tag: string): ItemDef[] {
    return this.list().filter((d) => d.tags.includes(tag));
  }

  /** The tab an item sorts into. Throws on an unknown id. */
  tabOf(id: string): string {
    return this.get(id).tab ?? DEFAULT_TAB;
  }

  /** The highest level an item reaches at a forge (1 when it has no levels). */
  maxLevel(id: string): number {
    return Math.max(1, this.get(id).levels?.length ?? 1);
  }

  /** The modifiers an item adds while equipped at a level: its own, then that level's. */
  modifiersAt(id: string, level = 1): Modifier[] {
    const d = this.get(id);
    const l = d.levels?.[Math.max(1, Math.min(level, this.maxLevel(id))) - 1];
    return [...(d.modifiers ?? []), ...(l?.modifiers ?? [])];
  }
}
